import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"; 
import {
  getChadawas,
  addChadawa as addChadawaService,
  updateChadawa as updateChadawaService,
  deleteChadawa as deleteChadawaService,
  updateChadawaStatus as updateChadawaStatusService,
} from "../../services/chadawaService";
import { toast } from "react-toastify";

const initialState = {
  chadawas: [],
  status: "idle",
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  }
};

// Fetch all chadawas with filters
export const fetchChadawas = createAsyncThunk(
  "chadawas/fetchChadawas",
  async (params = {}, { rejectWithValue }) => {

    try { 
      const response = await getChadawas(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch chadawas";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Add a new chadawa
export const addChadawa = createAsyncThunk(
  "chadawas/addChadawa",
  async (chadawaData, { rejectWithValue }) => {
    try {
      const response = await addChadawaService(chadawaData);
      toast.success(response.message || "Chadawa added successfully!");
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to add chadawa";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update a chadawa
export const updateChadawa = createAsyncThunk(
  "chadawas/updateChadawa",
  async ({ id, chadawaData }, { rejectWithValue }) => {
    try {
      const response = await updateChadawaService(id, chadawaData);
      toast.success(response.message || "Chadawa updated successfully!");
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update chadawa";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update a chadawa status
export const updateChadawaStatus = createAsyncThunk(
  "chadawas/updateChadawaStatus",
  async ({ id, statusObj }, { rejectWithValue }) => {
    try {
      const response = await updateChadawaStatusService(id, statusObj);
      toast.success(response.message || "Status updated successfully!");
      return { id, statusObj, updatedData: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update status";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete a chadawa
export const deleteChadawa = createAsyncThunk(
  "chadawas/deleteChadawa",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteChadawaService(id);
      toast.success(response.message || "Chadawa deleted successfully!");
      return id;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to delete chadawa";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const chadawaSlice = createSlice({
  name: "chadawas",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetChadawaState: (state) => {
      state.chadawas = [];
      state.status = "idle";
      state.error = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    },
    setChadawas: (state, action) => {
      state.chadawas = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Chadawas
      .addCase(fetchChadawas.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchChadawas.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.chadawas = action.payload.data || [];
        
        // Store backend pagination
        state.pagination = action.payload.pagination || {
          total: state.chadawas.length,
          page: 1,
          limit: state.chadawas.length,
          totalPages: 1
        };

        state.error = null;
      })
      .addCase(fetchChadawas.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.chadawas = [];
        state.pagination = {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        };
      })

      // Add Chadawa
      .addCase(addChadawa.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addChadawa.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.data) {
          state.chadawas.unshift(action.payload.data);
          state.pagination.total += 1;
        }
        state.error = null;
      })
      .addCase(addChadawa.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Chadawa
      .addCase(updateChadawa.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateChadawa.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedChadawa = action.payload.data;

        if (updatedChadawa && Array.isArray(state.chadawas)) {
          const index = state.chadawas.findIndex((chadawa) => chadawa._id === updatedChadawa._id);
          if (index !== -1) {
            state.chadawas[index] = updatedChadawa;
          }
        }
        state.error = null;
      })
      .addCase(updateChadawa.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Chadawa Status
      .addCase(updateChadawaStatus.pending, (state) => {})
      .addCase(updateChadawaStatus.fulfilled, (state, action) => {
        const { id, statusObj, updatedData } = action.payload;

        if (Array.isArray(state.chadawas)) {
          const index = state.chadawas.findIndex((chadawa) => chadawa._id === id);
          if (index !== -1) {
            if (updatedData) {
              state.chadawas[index] = updatedData;
            } else {
              state.chadawas[index] = { ...state.chadawas[index], ...statusObj };
            }
          }
        }
      })
      .addCase(updateChadawaStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete Chadawa
      .addCase(deleteChadawa.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteChadawa.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload;
        state.chadawas = state.chadawas.filter((chadawa) => chadawa._id !== deletedId);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.error = null;
      })
      .addCase(deleteChadawa.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError, resetChadawaState, setChadawas } = chadawaSlice.actions;

// Selectors
export const selectAllChadawas = (state) => state.chadawas.chadawas;
export const selectChadawaById = (state, chadawaId) => 
  state.chadawas.chadawas.find(chadawa => chadawa._id === chadawaId);
export const selectChadawaStatus = (state) => state.chadawas.status;
export const selectChadawaError = (state) => state.chadawas.error;
export const selectChadawaPagination = (state) => state.chadawas.pagination;
export const selectActiveChadawas = (state) => 
  state.chadawas.chadawas.filter(chadawa => chadawa.status === 'Active');
export const selectPopularChadawas = (state) => 
  state.chadawas.chadawas.filter(chadawa => chadawa.isPopular === true);

export default chadawaSlice.reducer;